import { Enums } from '@bode-canada/core';
import {
  DateFormatHelper,
  DateOptionsBuilder,
  MonthFormats,
  YearFormats,
} from '@/ui-kit/helpers/DateFormatHelper';
import NumberFormat, { CurrencyCode } from '@/ui-kit/helpers/NumberFormatHelper';

const DateFormat = new DateOptionsBuilder()
  .addMonthFormat(MonthFormats.Long)
  .addYearFormat(YearFormats.Numeric)
  .build();
const PERCENTAGE = 100;
const DOES_NOT_EXIST_VALUE = 'N/A';
const HIDDEN_PRICE_SYMBOL = '*';
const NUMBER_REGEXP = /\d/g;

export default class PropertyTransaction {
  #isPriceHidden = null;

  constructor(transactionDto, isPriceHidden = false) {
    Object.assign(this, transactionDto);
    this.#isPriceHidden = isPriceHidden;
  }

  get isSold() {
    return this.status === Enums.ListingStatusEnum.SOLD;
  }

  get soldDate() {
    return PropertyTransaction.#formatDate(this.isSold && this.closedAt);
  }

  get formattedCloseDate() {
    return PropertyTransaction.#formatDate(this.closedAt);
  }

  get publishedDate() {
    return PropertyTransaction.#formatDate(this.publishedAt);
  }

  get soldPrice() {
    return PropertyTransaction.#formatPrice(this.isSold && this.price, this.#isPriceHidden);
  }

  get formattedPrice() {
    return PropertyTransaction.#formatPrice(this.price, this.#isPriceHidden);
  }

  get originalListPrice() {
    return PropertyTransaction.#formatPrice(this.listPrice, this.#isPriceHidden);
  }

  get priceRatio() {
    if (!this.price || !this.listPrice || !this.isSold) {
      return DOES_NOT_EXIST_VALUE;
    }

    const ratio = Math.round((this.price / this.listPrice) * PERCENTAGE).toString();

    return PropertyTransaction.#formatNumericString(`${ratio}%`, this.#isPriceHidden);
  }

  get sellerSavings() {
    if (!this.commissionSavings) {
      return null;
    }

    return PropertyTransaction.#formatPrice(this.commissionSavings, this.#isPriceHidden);
  }

  static #formatDate(date) {
    return date ? DateFormatHelper.format(date, DateFormat) : DOES_NOT_EXIST_VALUE;
  }

  static #formatPrice(price, isPriceHidden) {
    if (!price) {
      return DOES_NOT_EXIST_VALUE;
    }

    const formattedPrice = NumberFormat.formatCurrency(
      price,
      CurrencyCode.CanadianDollars,
    );

    return this.#formatNumericString(formattedPrice, isPriceHidden);
  }

  static #formatNumericString(string, areSymbolsHidden) {
    return areSymbolsHidden
      ? this.#hideNumericSymbols(string)
      : string;
  }

  static #hideNumericSymbols(string) {
    return string.replace(NUMBER_REGEXP, HIDDEN_PRICE_SYMBOL);
  }
}
